import * as React from "react";
import config from "../config";
import { ItemService } from "../services/itemService";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Box,
  Typography,
  Paper,
  PaperProps,
  TextField,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  Alert,
  Tooltip,
  Snackbar,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Draggable from "react-draggable";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Context } from "./context";
import { MeetingItemStatus, ApiUser, UserRole } from "../models";
import { MeetingItem, RiskIndicator, MeetingItemType } from "../models";
import { bindActionCreators } from "../actions/actionCreators";
import { FreeSingleSelect } from "./freeSingleSelect";
import ConfirmationPanel from "./confirmationPanel";
import { bulkCreateUsers } from "../services/userService";
import CheckIcon from "@mui/icons-material/Check";
import dayjs from "dayjs";
import { PaperComponent } from "../util/paperComponent";
import * as userActions from "../actions/userActions";

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function QuickTasksPanel({ open, onClose }: Props) {
  // Save and keep dialog open for additional tasks
  const handleSaveAndContinue = async () => {
    try {
      if (isTaskForOther && responsible.trim()) {
        const trimmedValue = responsible.trim();
        if (isValidEmail(trimmedValue)) {
          await createUserIfNeeded(trimmedValue);
        }
      }
      const newTask: MeetingItem = {
        content: description.trim(),
        category: category.trim() || undefined,
        actor: isTaskForOther
          ? responsible.trim()
          : appContext.state.invoker?.email,
        dueDate: dueDate,
        status,
        risk: RiskIndicator.NoRisk,
        types: [MeetingItemType.Task],
        meetingId: undefined,
        creator: appContext.state.invoker?.email,
      };
      const itemService = new ItemService(config.api.baseUrl, "/quicktask");
      await itemService.save(newTask);
      // Capture entered values before reset
      const enteredCategory = (category || "").trim();
      const enteredEmail = (responsible || "").trim();
      // Show success notification and reset immediately
      setSuccessSnackbarOpen(true);
      resetForm();
      // Compute immediate invoker update for instant suggestions and usage bump on save
      const invoker = appContext.state.invoker;
      if (invoker) {
        let changed = false;
        const currentCategorySuggestions = (invoker.categorySuggestions ||
          []) as Array<{ value: string; count: number }>;
        const currentEmailSuggestions = (invoker.emailSuggestions ||
          []) as Array<{ value: string; count: number }>;
        const nextInvoker = { ...invoker };
        const upsert = (
          arr: Array<{ value: string; count: number }>,
          val?: string,
        ) => {
          if (!val) return arr;
          const v = val.toString();
          const idx = arr.findIndex((e) => e.value === v);
          if (idx >= 0) {
            const next = [...arr];
            next[idx] = { value: v, count: (next[idx]?.count ?? 0) + 1 };
            return next;
          }
          return [...arr, { value: v, count: 1 }];
        };
        if (enteredCategory) {
          nextInvoker.categorySuggestions = upsert(
            currentCategorySuggestions,
            enteredCategory,
          );
          changed = true;
        }
        if (isTaskForOther && enteredEmail && isValidEmail(enteredEmail)) {
          nextInvoker.emailSuggestions = upsert(
            currentEmailSuggestions,
            enteredEmail,
          );
          changed = true;
        }
        if (changed) {
          // Optimistically update context so suggestions appear immediately
          appContext.dispatch(userActions.setInvokerAction(nextInvoker));
          // Persist changes in background (non-blocking)
          (async () => {
            try {
              await actions.saveInvoker(nextInvoker);
              await actions.refreshInvoker();
            } catch (e) {
              console.warn("Failed to update suggestions:", e);
            }
          })();
        }
      }
      // Keep dialog open for additional tasks
    } catch (error) {
      console.error("Failed to save task:", error);
    }
  };
  const { t } = useTranslation();
  const appContext = React.useContext(Context);
  const actions = React.useMemo(
    () => ({
      saveInvoker: userActions.save,
      refreshInvoker: () => userActions.getInvoker()(appContext.dispatch),
    }),
    [appContext.dispatch],
  );
  const currentEmailSuggestions = React.useMemo(
    () => appContext.state.invoker?.emailSuggestions || [],
    [appContext.state.invoker?.emailSuggestions],
  );
  const [warningDialogOpen, setWarningDialogOpen] = React.useState(false);
  const [description, setDescription] = React.useState("");
  const [category, setCategory] = React.useState("");
  const [responsible, setResponsible] = React.useState("");
  const [dueDate, setDueDate] = React.useState<Date | null>(new Date());
  const [isTaskForOther, setIsTaskForOther] = React.useState(false);
  const [status, setStatus] = React.useState<MeetingItemStatus>(
    MeetingItemStatus.NotStarted,
  );
  const [emailError, setEmailError] = React.useState<string>("");
  const [successSnackbarOpen, setSuccessSnackbarOpen] = React.useState(false);

  // Ref to track if component is mounted (prevent state updates after unmount)
  const isMountedRef = React.useRef(true);

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Sync state when dialog opens
  React.useEffect(() => {
    if (open) {
      // Refresh invoker cache (email/category suggestions)
      actions.refreshInvoker();
      // Reset form when dialog opens
      setDescription("");
      setCategory("");
      setResponsible("");
      setDueDate(new Date());
      setIsTaskForOther(false);
      setStatus(MeetingItemStatus.NotStarted);
      setEmailError("");
      setWarningDialogOpen(false);
    }
  }, [open]);

  const userOptions = React.useMemo(() => {
    const options = new Set<string>();

    // Early return if no lists
    if (!appContext.state.lists) {
      return [];
    }

    const invokerEmail = appContext.state.invoker?.email;

    // Add users from meetings where invoker has had interactions
    appContext.state.lists.forEach((meeting) => {
      const hasInvokerInteraction =
        meeting.creator === invokerEmail ||
        meeting.participants?.includes(invokerEmail) ||
        meeting.viewers?.includes(invokerEmail);

      if (hasInvokerInteraction) {
        meeting.creator && options.add(meeting.creator);
        meeting.participants?.forEach((p) => options.add(p));
        meeting.viewers?.forEach((v) => options.add(v));
      }
    });

    return Array.from(options).sort();
  }, [appContext.state.lists, appContext.state.invoker?.email]);

  const isActor = React.useMemo(() => {
    return appContext.state.invoker?.role === UserRole.Actor;
  }, [appContext.state.invoker?.role]);

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setIsTaskForOther(checked);
    if (!checked) {
      setResponsible("");
      setEmailError("");
    }
  };

  const handleResponsibleChange = (value: string) => {
    setResponsible(value);
    setEmailError("");

    // Only validate when task is for another person
    if (!isTaskForOther) {
      return;
    }

    const trimmedValue = value.trim();

    if (trimmedValue === "") {
      return;
    }

    if (trimmedValue.includes("@")) {
      if (!isValidEmail(trimmedValue)) {
        setEmailError(t("quicktask.invalid_email"));
        return;
      }
    }
  };

  // Create user if email is valid and not already in existing options - Müsste nochmal drüber schauen (KI generierter Code mit mounted Ref)
  const createUserIfNeeded = async (email: string): Promise<boolean> => {
    try {
      const trimmedEmail = email.trim();

      const users: ApiUser[] = [
        {
          email: trimmedEmail,
          role: UserRole.Actor,
        },
      ];

      await bulkCreateUsers(users);

      // Only log success if component is still mounted
      if (isMountedRef.current) {
        console.log("User created successfully:", trimmedEmail);
      }

      return true;
    } catch (error) {
      console.warn("User creation failed or user already exists:", error);
      // Don't show error to user - user might already exist
      return true;
    }
  };

  const hasChanges = React.useMemo(() => {
    return (
      description.trim() !== "" ||
      category.trim() !== "" ||
      responsible.trim() !== "" ||
      (dueDate !== null && !dayjs(dueDate).isSame(dayjs(), "day"))
    );
  }, [description, category, responsible, dueDate]);

  const resetForm = React.useCallback(() => {
    setCategory("");
    setDescription("");
    setResponsible("");
    setDueDate(new Date());
    setStatus(MeetingItemStatus.NotStarted);
    setEmailError("");
  }, []);

  const handleWarningClose = React.useCallback(() => {
    if (hasChanges) {
      setWarningDialogOpen(true);
    } else {
      onClose();
      resetForm();
    }
  }, [hasChanges, onClose, resetForm]);

  // Email validation functions taken from itemsPage
  const isValidEmail = (email: string) => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  };

  const handleSave = async () => {
    try {
      if (isTaskForOther && responsible.trim()) {
        const trimmedValue = responsible.trim();
        if (isValidEmail(trimmedValue)) {
          await createUserIfNeeded(trimmedValue);
        }
      }

      const newTask: MeetingItem = {
        content: description.trim(),
        category: category.trim() || undefined,
        actor: isTaskForOther
          ? responsible.trim()
          : appContext.state.invoker?.email,
        dueDate: dueDate,
        status,
        risk: RiskIndicator.NoRisk,
        types: [MeetingItemType.Task],
        meetingId: undefined, // Standalone task, no meeting
        creator: appContext.state.invoker?.email,
      };

      // Use ItemService for quicktasks (no meetingId)
      const itemService = new ItemService(config.api.baseUrl, "/quicktask");
      const savedItem = await itemService.save(newTask);
      // Capture entered values before reset/close
      const enteredCategory = (category || "").trim();
      const enteredEmail = (responsible || "").trim();

      // Show success, reset and close immediately
      setSuccessSnackbarOpen(true);
      setIsTaskForOther(false);
      resetForm();
      onClose();

      // Update suggestions and usage in background (non-blocking)
      (async () => {
        try {
          const invoker = appContext.state.invoker;
          if (!invoker) return;
          let changed = false;
          const nextInvoker: typeof invoker = { ...invoker };
          const currentCategorySuggestions = (invoker.categorySuggestions ||
            []) as Array<{ value: string; count: number }>;
          const currentEmailSuggestions = (invoker.emailSuggestions ||
            []) as Array<{ value: string; count: number }>;
          const upsert = (
            arr: Array<{ value: string; count: number }>,
            val?: string,
          ) => {
            if (!val) return arr;
            const v = val.toString();
            const idx = arr.findIndex((e) => e.value === v);
            if (idx >= 0) {
              const next = [...arr];
              next[idx] = { value: v, count: (next[idx]?.count ?? 0) + 1 };
              return next;
            }
            return [...arr, { value: v, count: 1 }];
          };
          if (enteredCategory) {
            nextInvoker.categorySuggestions = upsert(
              currentCategorySuggestions,
              enteredCategory,
            );
            changed = true;
          }
          if (isTaskForOther && enteredEmail && isValidEmail(enteredEmail)) {
            nextInvoker.emailSuggestions = upsert(
              currentEmailSuggestions,
              enteredEmail,
            );
            changed = true;
          }
          if (changed) {
            await actions.saveInvoker(nextInvoker);
            await actions.refreshInvoker();
          }
        } catch (e) {
          console.warn("Failed to update suggestions:", e);
        }
      })();
    } catch (error) {
      console.error("Failed to save task:", error);
      // TODO: Add error handling/notification?
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleWarningClose}
        fullWidth
        maxWidth="md"
        keepMounted={false}
        PaperComponent={PaperComponent}
        aria-labelledby="draggable-dialog-title"
        slotProps={{
          paper: {
            style: {
              borderRadius: "8px",
              backgroundColor: "white",
            },
          },
        }}
      >
        <Box
          id="draggable-dialog-title"
          sx={{ display: "flex", alignItems: "center", cursor: "grab" }}
        >
          <DialogTitle sx={{ textAlign: "left" }}>
            {t("quicktask.title")}
          </DialogTitle>

          <IconButton
            aria-label={t("button.close")}
            onClick={handleWarningClose}
            sx={{
              position: "absolute",
              right: 12,
              color: "#5F6368",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent dividers>
          <Box
            component="form"
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            {/* task category with suggestions */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography
                variant="subtitle2"
                component="label"
                htmlFor="category-input"
              >
                {t("quicktask.category_title")}
              </Typography>
              <FreeSingleSelect
                onValueSelected={(value) => {
                  setCategory(value);
                }}
                options={[]}
                value={category}
                placeholder={t("quicktask.category_placeholder")}
                hideTooltip={true}
                maxVisibleItems={5}
                useCategorySuggestions={true}
                enableDelete={true}
                enableUsageSort={true}
              />
            </Box>
            {/* task description */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography
                variant="subtitle2"
                component="label"
                htmlFor="description-input"
              >
                {t("quicktask.description_title")} *
              </Typography>
              <TextField
                id="description-input"
                required
                placeholder={t("quicktask.description_placeholder")}
                multiline
                rows={5}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Box>
            {/* task responsible */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  component="label"
                  htmlFor="responsible-input"
                >
                  {t("quicktask.responsible_title")} *
                </Typography>
                <FreeSingleSelect
                  onValueSelected={handleResponsibleChange}
                  options={[]}
                  value={
                    isTaskForOther
                      ? responsible
                      : appContext.state.invoker?.email || ""
                  }
                  placeholder={t("quicktask.responsible_placeholder")}
                  disabled={!isTaskForOther}
                  error={!!emailError}
                  hideTooltip={true}
                  maxVisibleItems={5}
                  useEmailSuggestions={true}
                  enableDelete={true}
                  enableUsageSort={true}
                  dropdownMinWidth={340}
                />
                {emailError && (
                  <Alert
                    severity="error"
                    sx={{
                      padding: "4px 8px",
                      fontSize: "0.875rem",
                      wordBreak: "break-all",
                    }}
                    role="alert"
                  >
                    {emailError}
                  </Alert>
                )}
              </Box>
              {/* task due date */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Typography variant="subtitle2" component="label">
                  {t("quicktask.dueDate_title")}
                </Typography>
                <DatePicker
                  className="lighter-textfield"
                  value={dueDate ? dayjs(dueDate) : null}
                  onChange={(newDate) =>
                    setDueDate(newDate ? newDate.toDate() : null)
                  }
                  slotProps={{
                    textField: {
                      size: "small",
                      "aria-label": t("quicktask.dueDate_title"),
                    },
                  }}
                />
              </Box>
              {/* task status */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  component="label"
                  htmlFor="status-select"
                >
                  {t("quicktask.status_title")}
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    id="status-select"
                    value={status}
                    onChange={(e) =>
                      setStatus(e.target.value as MeetingItemStatus)
                    }
                    sx={{
                      "& .MuiSelect-select": {
                        fontFamily: "inherit",
                        fontSize: "1rem",
                        color: "black",
                      },
                    }}
                    MenuProps={{
                      slotProps: {
                        paper: {
                          sx: {
                            backgroundColor: "white",
                            "& .MuiMenuItem-root": {
                              fontSize: "1rem",
                              fontFamily: "inherit",
                            },
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value={MeetingItemStatus.NotStarted}>
                      {t(`item_status.${MeetingItemStatus.NotStarted}`)}
                    </MenuItem>
                    <MenuItem value={MeetingItemStatus.InProgress}>
                      {t(`item_status.${MeetingItemStatus.InProgress}`)}
                    </MenuItem>
                    <MenuItem value={MeetingItemStatus.OnHold}>
                      {t(`item_status.${MeetingItemStatus.OnHold}`)}
                    </MenuItem>
                    <MenuItem value={MeetingItemStatus.Completed}>
                      {t(`item_status.${MeetingItemStatus.Completed}`)}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ px: 3, py: 2, display: "flex", alignItems: "center" }}
        >
          <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
            <Tooltip title={isActor ? t("quicktask.switch_disabled") : ""}>
              <span>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isTaskForOther}
                      onChange={handleSwitchChange}
                      disabled={isActor}
                      slotProps={{
                        input: { "aria-describedby": "switch-helper" },
                      }}
                    />
                  }
                  label={t("quicktask.switch_placeholder")}
                />
              </span>
            </Tooltip>
          </Box>
          <Tooltip
            title={
              !description.trim() ||
              (isTaskForOther && (!responsible.trim() || !!emailError))
                ? t("quicktask.fill_mandatory_fields")
                : ""
            }
          >
            <span>
              <Button
                variant="outlined"
                autoFocus
                onClick={handleSaveAndContinue}
                disabled={
                  !description.trim() ||
                  (isTaskForOther && (!responsible.trim() || !!emailError))
                }
              >
                {t("quicktask.additionaltask_button")}
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              !description.trim() ||
              (isTaskForOther && (!responsible.trim() || !!emailError))
                ? t("quicktask.fill_mandatory_fields")
                : ""
            }
          >
            <span>
              <Button
                variant="contained"
                autoFocus
                onClick={handleSave}
                disabled={
                  !description.trim() ||
                  (isTaskForOther && (!responsible.trim() || !!emailError))
                }
              >
                {t("button.save")}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
      {/* Unsaved Changes Warning Dialog */}
      <ConfirmationPanel
        action="warning"
        showModal={warningDialogOpen}
        hideModal={() => setWarningDialogOpen(false)}
        confirmModal={() => {
          setWarningDialogOpen(false);
          resetForm();
          onClose();
        }}
      />
      {/* Success Snackbar */}
      <Snackbar
        open={successSnackbarOpen}
        autoHideDuration={3500}
        onClose={() => setSuccessSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
          icon={<CheckIcon fontSize="inherit" />}
        >
          {t("quicktask.success_message")}
        </Alert>
      </Snackbar>
    </>
  );
}
